import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageCircle,
  Plus,
  Search,
  Send,
  Users,
  User,
  GraduationCap,
  Loader2,
  ChevronLeft,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const threadTypeConfig = {
  direkt: { label: 'Direkt Mesaj', icon: User },
  grup: { label: 'Grup', icon: Users },
  sinif: { label: 'Sınıf', icon: GraduationCap },
  ogrenci_tartismasi: { label: 'Öğrenci Tartışması', icon: User },
};

const MessagesPage = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewThread, setShowNewThread] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const messagesEndRef = useRef(null);
  
  const [newThread, setNewThread] = useState({
    subject: '',
    participant_ids: [],
    thread_type: 'direkt',
    initial_message: '',
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchThreads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/messages/threads`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setThreads(data);
      }
    } catch (error) {
      console.error('Error fetching threads:', error);
      toast.error('Mesajlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    setMessagesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/messages/threads/${threadId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        // Refresh threads to update unread counts
        fetchThreads();
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.filter(u => u.user_id !== user?.user_id));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/messages/unread-count`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setTotalUnread(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  useEffect(() => {
    fetchThreads();
    fetchUsers();
    fetchUnreadCount();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread.thread_id);
    }
  }, [selectedThread]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    setSendingMessage(true);
    try {
      const response = await fetch(`${API_URL}/api/messages/threads/${selectedThread.thread_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage }),
      });

      if (response.ok) {
        const msg = await response.json();
        setMessages(prev => [...prev, msg]);
        setNewMessage('');
        fetchThreads();
      }
    } catch (error) {
      toast.error('Mesaj gönderilemedi');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateThread = async () => {
    if (!newThread.subject.trim() || !newThread.initial_message.trim() || newThread.participant_ids.length === 0) {
      toast.error('Konu, katılımcı ve mesaj zorunludur');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/messages/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newThread),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Sohbet oluşturuldu');
        setShowNewThread(false);
        setNewThread({
          subject: '',
          participant_ids: [],
          thread_type: 'direkt',
          initial_message: '',
        });
        fetchThreads();
        // Select new thread
        const threadsRes = await fetch(`${API_URL}/api/messages/threads`, {
          credentials: 'include',
        });
        if (threadsRes.ok) {
          const threadsData = await threadsRes.json();
          const newT = threadsData.find(t => t.thread_id === result.thread_id);
          if (newT) setSelectedThread(newT);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Sohbet oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const toggleParticipant = (userId) => {
    setNewThread(prev => ({
      ...prev,
      participant_ids: prev.participant_ids.includes(userId)
        ? prev.participant_ids.filter(id => id !== userId)
        : [...prev.participant_ids, userId]
    }));
  };

  const filteredThreads = threads.filter(t =>
    t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.participants?.some(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getOtherParticipants = (thread) => {
    return thread.participants?.filter(p => p.user_id !== user?.user_id) || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex" data-testid="messages-page">
      {/* Thread List */}
      <div className={`w-full md:w-80 flex-shrink-0 border-r flex flex-col ${
        selectedThread ? 'hidden md:flex' : 'flex'
      }`}>
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Mesajlar
              {totalUnread > 0 && (
                <Badge>{totalUnread}</Badge>
              )}
            </h2>
            <Dialog open={showNewThread} onOpenChange={setShowNewThread}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="new-thread-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Yeni Sohbet</DialogTitle>
                  <DialogDescription>
                    Meslektaşlarınızla yeni bir sohbet başlatın
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Konu *</Label>
                    <Input
                      value={newThread.subject}
                      onChange={(e) => setNewThread({...newThread, subject: e.target.value})}
                      placeholder="Sohbet konusu"
                    />
                  </div>
                  <div>
                    <Label>Katılımcılar *</Label>
                    <div className="max-h-40 overflow-y-auto border rounded-lg p-2 mt-1">
                      {users.map(u => (
                        <label
                          key={u.user_id}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${
                            newThread.participant_ids.includes(u.user_id) ? 'bg-primary/10' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={newThread.participant_ids.includes(u.user_id)}
                            onChange={() => toggleParticipant(u.user_id)}
                            className="rounded"
                          />
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="text-xs">{u.name?.[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{u.name}</span>
                          <Badge variant="outline" className="text-xs ml-auto">{u.role}</Badge>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>İlk Mesaj *</Label>
                    <Textarea
                      value={newThread.initial_message}
                      onChange={(e) => setNewThread({...newThread, initial_message: e.target.value})}
                      placeholder="Mesajınızı yazın..."
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowNewThread(false)}>
                    İptal
                  </Button>
                  <Button onClick={handleCreateThread}>
                    Sohbet Başlat
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Sohbet ara..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          {filteredThreads.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              Henüz sohbet yok
            </div>
          ) : (
            filteredThreads.map(thread => {
              const others = getOtherParticipants(thread);
              const TypeIcon = threadTypeConfig[thread.thread_type]?.icon || MessageCircle;
              
              return (
                <button
                  key={thread.thread_id}
                  className={`w-full p-4 text-left border-b hover:bg-slate-50 transition-colors ${
                    selectedThread?.thread_id === thread.thread_id ? 'bg-primary/5' : ''
                  } ${thread.unread_count > 0 ? 'bg-blue-50' : ''}`}
                  onClick={() => setSelectedThread(thread)}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      {others.length === 1 ? (
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={others[0].picture} />
                          <AvatarFallback>{others[0].name?.[0]}</AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                          <TypeIcon className="h-5 w-5 text-slate-500" />
                        </div>
                      )}
                      {thread.unread_count > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                          {thread.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm truncate">
                          {others.length === 1 ? others[0].name : thread.subject}
                        </span>
                        {thread.last_message_at && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(thread.last_message_at).toLocaleDateString('tr-TR')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {thread.subject}
                      </p>
                      {thread.last_message && (
                        <p className="text-xs text-muted-foreground truncate mt-1">
                          {thread.last_message.sender_name}: {thread.last_message.content}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </ScrollArea>
      </div>

      {/* Message Area */}
      <div className={`flex-1 flex flex-col ${
        selectedThread ? 'flex' : 'hidden md:flex'
      }`}>
        {selectedThread ? (
          <>
            {/* Header */}
            <div className="p-4 border-b flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setSelectedThread(null)}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h3 className="font-semibold">{selectedThread.subject}</h3>
                <p className="text-sm text-muted-foreground">
                  {getOtherParticipants(selectedThread).map(p => p.name).join(', ')}
                </p>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messagesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map(msg => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex gap-2 max-w-[75%] ${
                        msg.sender_id === user?.user_id ? 'flex-row-reverse' : ''
                      }`}>
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarImage src={msg.sender_picture} />
                          <AvatarFallback className="text-xs">{msg.sender_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className={`rounded-lg p-3 ${
                            msg.sender_id === user?.user_id
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-slate-100'
                          }`}>
                            {msg.sender_id !== user?.user_id && (
                              <p className="text-xs font-medium mb-1">{msg.sender_name}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          <p className={`text-xs text-muted-foreground mt-1 ${
                            msg.sender_id === user?.user_id ? 'text-right' : ''
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString('tr-TR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Mesajınızı yazın..."
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                />
                <Button onClick={handleSendMessage} disabled={sendingMessage || !newMessage.trim()}>
                  {sendingMessage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Sohbet seçin veya yeni bir sohbet başlatın</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
